import { LuCopy, LuShieldCheck, LuTrash2 } from 'react-icons/lu'
import { Badge, Button, CodeInput, Form, MethodCard, Skeleton, Typography } from '@cannect/new-components/atoms'
import { ChangeEvent, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
  enrollEmailConfirm,
  enrollEmailStart,
  enrollSmsConfirm,
  enrollSmsStart,
  enrollTotpConfirm,
  enrollTotpStart,
  getMfaMethods,
  regenerateRecoveryCodes,
  removeMfaMethod
} from 'usecases/mfa'
import { TMfaMethodType } from 'dtos/User'
import { MFA_METHOD_DESCRIPTION, MFA_METHOD_ICON, MFA_METHOD_LABEL } from '@cannect/utils/mfaMethodPresentation'

// Custo de SMS (AWS) inviável — desativado por flag reversível, código
// preservado para reativação futura.
const isMfaSmsEnabled = import.meta.env.VITE_APP_MFA_SMS_ENABLED === 'true'

type TStep =
  | { kind: 'list' }
  | { kind: 'step_up'; action: 'remove' | 'regenerate' | 'add'; method?: TMfaMethodType }
  | { kind: 'enroll_select' }
  | {
      kind: 'enroll_confirm'
      method: TMfaMethodType
      enrollmentId: string
      maskedTarget: string | null
      secret?: string
      otpauthUri?: string
    }
  | { kind: 'recovery_codes'; codes: string[] }

// CA1/CA3/CA12/CA14 (feature portal-cannect-2fa) — reaproveita 100% os
// endpoints /v1/mfa/* da feature 2fa-auth, sem mudança nenhuma no backend.
// Todo endpoint sensível (remover método, regenerar recovery codes,
// adicionar método quando já existe outro ativo) exige step-up de senha
// (CA14) via `current_password` no próprio body — sem token de step-up
// separado (mesmo desenho da nota 3 §5 da feature 2fa-auth).
function TwoFactorSettings() {
  const [methods, setMethods] = useState<{ type: TMfaMethodType; active_since: string }[]>([])
  const [loadingList, setLoadingList] = useState(true)
  const [step, setStep] = useState<TStep>({ kind: 'list' })
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const loadMethods = async () => {
    setLoadingList(true)
    try {
      const data = await getMfaMethods()
      setMethods(data.methods)
    } catch {
      toast.error('Não foi possível carregar seus métodos de autenticação.')
    } finally {
      setLoadingList(false)
    }
  }

  useEffect(() => {
    loadMethods()
  }, [])

  const resetToList = () => {
    setStep({ kind: 'list' })
    setPassword('')
    setCode('')
    setErrorMessage('')
  }

  const startEnrollFor = async (method: TMfaMethodType, currentPassword?: string) => {
    setBusy(true)
    setErrorMessage('')
    try {
      if (method === 'totp') {
        const data = await enrollTotpStart(currentPassword)
        setStep({
          kind: 'enroll_confirm',
          method,
          enrollmentId: data.enrollment_id,
          maskedTarget: null,
          secret: data.secret,
          otpauthUri: data.otpauth_uri
        })
        return
      }
      const data = method === 'email' ? await enrollEmailStart(currentPassword) : await enrollSmsStart(currentPassword)
      setStep({ kind: 'enroll_confirm', method, enrollmentId: data.enrollment_id, maskedTarget: data.masked_target })
    } catch (error: any) {
      const apiCode = error?.response?.data?.code
      if (apiCode === 'INVALID_STEP_UP') {
        setErrorMessage('Senha atual incorreta.')
      } else if (apiCode === 'PHONE_UNAVAILABLE') {
        setErrorMessage('Cadastre um celular válido antes de ativar o SMS.')
      } else {
        setErrorMessage('Não foi possível iniciar a configuração deste método.')
      }
    } finally {
      setBusy(false)
    }
  }

  const onChooseAddMethod = (method: TMfaMethodType) => {
    setStep({ kind: 'step_up', action: 'add', method })
  }

  const confirmStepUp = async () => {
    if (step.kind !== 'step_up') return
    if (step.action === 'add' && step.method) {
      await startEnrollFor(step.method, password)
      return
    }
    if (step.action === 'remove' && step.method) {
      setBusy(true)
      setErrorMessage('')
      try {
        await removeMfaMethod(step.method, password)
        toast.success('Método removido.')
        resetToList()
        loadMethods()
      } catch (error: any) {
        const apiCode = error?.response?.data?.code
        setErrorMessage(
          apiCode === 'CANNOT_REMOVE_LAST_METHOD'
            ? 'Configure outro método antes de remover o único ativo — 2FA é obrigatório.'
            : 'Senha atual incorreta.'
        )
      } finally {
        setBusy(false)
      }
      return
    }
    if (step.action === 'regenerate') {
      setBusy(true)
      setErrorMessage('')
      try {
        const data = await regenerateRecoveryCodes(password)
        setStep({ kind: 'recovery_codes', codes: data.recovery_codes })
        setPassword('')
      } catch {
        setErrorMessage('Senha atual incorreta.')
      } finally {
        setBusy(false)
      }
    }
  }

  const confirmEnroll = async (submittedCode: string = code) => {
    if (step.kind !== 'enroll_confirm') return
    setBusy(true)
    setErrorMessage('')
    try {
      const confirmFnByMethod = {
        email: enrollEmailConfirm,
        sms: enrollSmsConfirm,
        totp: enrollTotpConfirm
      }
      const data = await confirmFnByMethod[step.method](step.enrollmentId, submittedCode)
      if (data.recovery_codes) {
        setStep({ kind: 'recovery_codes', codes: data.recovery_codes })
      } else {
        toast.success('Método configurado com sucesso!')
        resetToList()
      }
      loadMethods()
    } catch {
      setErrorMessage('Código inválido ou expirado.')
    } finally {
      setBusy(false)
    }
  }

  const copyToClipboard = async (text: string, message: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success(message)
    } catch {
      toast.error('Não foi possível copiar. Copie manualmente.')
    }
  }

  return (
    <div className="flex min-h-screen w-full flex-1 flex-col bg-gray-50 p-4 sm:p-6 lg:p-8">
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 rounded-lg bg-white p-4 shadow-sm sm:p-6 lg:p-10">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
              <LuShieldCheck size={20} />
            </span>
            <Typography.Heading type="headingFive" weight="semibold" className="text-lg sm:text-xl">
              Autenticação em duas etapas
            </Typography.Heading>
          </div>
          <Typography.Text muted>
            Métodos ativos protegem sua conta com um segundo fator no login, além da senha.
          </Typography.Text>
        </div>

        {step.kind === 'list' && (
          <>
            {loadingList ? (
              <div className="flex flex-col gap-3">
                <Skeleton className="h-[72px] w-full rounded-xl" />
                <Skeleton className="h-[72px] w-full rounded-xl" />
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {methods.length === 0 && <Typography.Text muted>Nenhum método configurado ainda.</Typography.Text>}
                {methods.map((method) => (
                  <div
                    key={method.type}
                    className="flex flex-col items-start gap-3 rounded-xl border border-gray-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-3.5">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
                        {MFA_METHOD_ICON[method.type]}
                      </span>
                      <div className="flex flex-col items-start gap-0.5">
                        <span className="text-sm font-semibold text-gray-900">{MFA_METHOD_LABEL[method.type]}</span>
                        <Badge variant="success" size="xs">
                          Ativo
                        </Badge>
                      </div>
                    </div>
                    {methods.length > 1 && (
                      <Button
                        variant="outline"
                        size="md"
                        className="border-red-600 text-red-600 hover:opacity-80"
                        icon={<LuTrash2 size={16} />}
                        iconPlacement="left"
                        onClick={() => setStep({ kind: 'step_up', action: 'remove', method: method.type })}>
                        Remover
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-col gap-3 sm:flex-row">
              <Button onClick={() => setStep({ kind: 'enroll_select' })}>Adicionar método</Button>
              {methods.length > 0 && (
                <Button variant="outline_light" onClick={() => setStep({ kind: 'step_up', action: 'regenerate' })}>
                  Gerar novos códigos de recuperação
                </Button>
              )}
            </div>
          </>
        )}

        {step.kind === 'enroll_select' && (
          <>
            <Typography.Text muted>Escolha o método para adicionar</Typography.Text>
            <div className="flex flex-col gap-3">
              {(['email', 'sms', 'totp'] as TMfaMethodType[])
                .filter((method) => !methods.some((m) => m.type === method))
                .filter((method) => isMfaSmsEnabled || method !== 'sms')
                .filter((method) => method !== 'totp' || methods.some((m) => m.type === 'email'))
                .map((method) => (
                  <MethodCard
                    key={method}
                    icon={MFA_METHOD_ICON[method]}
                    title={MFA_METHOD_LABEL[method]}
                    description={MFA_METHOD_DESCRIPTION[method]}
                    onClick={() => onChooseAddMethod(method)}
                  />
                ))}
            </div>
            <Button variant="outline_light" className="self-start" onClick={resetToList}>
              Cancelar
            </Button>
          </>
        )}

        {step.kind === 'step_up' && (
          <>
            <Typography.Text muted>Confirme sua senha para continuar</Typography.Text>
            <Form.Input
              variant="filledLight"
              label="Senha atual"
              value={password}
              onChange={(event: ChangeEvent<HTMLInputElement>) => setPassword(event.target.value)}
              type="password"
            />
            {errorMessage && <p className="-mt-2 text-sm text-red-600">{errorMessage}</p>}
            <div className="flex flex-col gap-3 sm:flex-row">
              <Button isLoading={busy} onClick={confirmStepUp} disabled={!password}>
                Confirmar
              </Button>
              <Button variant="outline_light" onClick={resetToList}>
                Cancelar
              </Button>
            </div>
          </>
        )}

        {step.kind === 'enroll_confirm' && (
          <>
            {step.method === 'totp' && step.otpauthUri && (
              <>
                <Typography.Text muted>
                  Adicione esta chave no seu aplicativo autenticador (Google Authenticator, Authy, etc.):
                </Typography.Text>
                <button
                  type="button"
                  onClick={() => step.secret && copyToClipboard(step.secret, 'Chave copiada.')}
                  className="group flex appearance-none items-center justify-between gap-2 rounded-md border border-solid border-gray-200 bg-gray-50 px-3 py-2.5 text-left outline-none transition-colors hover:border-primary-300 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700 focus-visible:ring-offset-2">
                  <code className="break-all font-mono text-sm text-gray-900">{step.secret}</code>
                  <LuCopy
                    className="shrink-0 text-gray-400 group-hover:text-primary-700"
                    size={16}
                    aria-hidden="true"
                  />
                </button>
              </>
            )}
            {step.method !== 'totp' && (
              <Typography.Text muted>Enviamos um código de 6 dígitos para {step.maskedTarget}.</Typography.Text>
            )}
            <CodeInput
              value={code}
              onChange={setCode}
              onComplete={confirmEnroll}
              ariaLabel="Código de confirmação de 6 dígitos"
              error={!!errorMessage}
              disabled={busy}
              autoFocus
            />
            {errorMessage && <p className="-mt-2 text-sm text-red-600">{errorMessage}</p>}
            <div className="flex flex-col gap-3 sm:flex-row">
              <Button isLoading={busy} onClick={() => confirmEnroll()} disabled={code.length !== 6}>
                Confirmar
              </Button>
              <Button variant="outline_light" onClick={resetToList}>
                Cancelar
              </Button>
            </div>
          </>
        )}

        {step.kind === 'recovery_codes' && (
          <>
            <Typography.Text muted>
              Guarde estes códigos de recuperação. Cada um só pode ser usado uma vez e eles não serão mostrados
              novamente. Toque em um código para copiá-lo.
            </Typography.Text>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {step.codes.map((recoveryCode) => (
                <button
                  key={recoveryCode}
                  type="button"
                  onClick={() => copyToClipboard(recoveryCode, 'Código copiado.')}
                  className="group flex appearance-none items-center justify-between gap-1.5 rounded-md border border-solid border-gray-200 bg-gray-50 px-3 py-2 text-left outline-none transition-colors hover:border-primary-300 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700 focus-visible:ring-offset-2">
                  <code className="break-all font-mono text-sm text-gray-900">{recoveryCode}</code>
                  <LuCopy
                    className="shrink-0 text-gray-400 group-hover:text-primary-700"
                    size={14}
                    aria-hidden="true"
                  />
                </button>
              ))}
            </div>
            <Button onClick={resetToList}>Já guardei, continuar</Button>
          </>
        )}
      </div>
    </div>
  )
}

export default TwoFactorSettings
